import { CONFIG } from "../config";
import type { Judgement } from "../engine/scoring";
import { chordLabel } from "../songs/chord-atoms";
import type { NoteEvent, Song } from "../types";
import { classifyNoteValue, type NoteValue } from "./note-value";
import { resolveKey } from "./staff-key";
import {
  type Accidental,
  type Clef,
  flowNoteX,
  pageVisible,
  staffLayoutFor,
  staffPos,
  staffStepY,
} from "./staff-layout";
import type { StageRects } from "./stage-layout";

// Schematic sight-reading staff: a pure draw-model builder (`staffFrame`) plus a
// thin canvas blit (`drawStaff`). Note state derives from the engine's existing
// per-note judgement; wrong-note overlays come from the scorer's recent-wrong ring.

export type StaffNoteState = "future" | "active" | "hit" | "miss" | "wrong";

export interface PlacedNote {
  x: number;
  y: number;
  state: StaffNoteState;
  /** 0..1 draw opacity (proximity ramp / blink / fade). */
  alpha: number;
  accidental: Accidental;
  clef: Clef;
  /** Y of each ledger line for a note outside its staff's five lines (else empty). */
  ledgers: number[];
  /** Note-value glyph; null = unknown (no beat grid) → plain filled dot. */
  value: NoteValue | null;
  dotted: boolean;
  /** Y of this note's staff middle line (for stem direction). */
  middleY: number;
  beamed: boolean;
  /** When beamed, the y the stem extends to (the beam level); else null. */
  stemTipY: number | null;
}

export interface StaffSystemBox {
  /** Y of each clef's middle line, for drawing the five lines + clef glyph. */
  trebleMiddleY: number;
  bassMiddleY: number;
  x0: number;
  x1: number;
}

export interface StaffFrameModel {
  systems: StaffSystemBox[];
  noteheads: PlacedNote[];
  nowMarker: { x: number; y0: number; y1: number } | null;
  /** Vertical measure barlines (grand-staff connecting), absolute canvas coords. */
  barlines: { x: number; y0: number; y1: number }[];
  /** Beams over grouped eighth/sixteenth runs — a slanted segment (x0,y0)→(x1,y1),
   *  with an optional secondary beam over the sixteenth-note sub-span. */
  beams: {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
    up: boolean;
    secondary: { x0: number; x1: number } | null;
    state: StaffNoteState;
    alpha: number;
  }[];
  /** Lead-sheet chord symbols above the treble staff — one per chorded, visible bar.
   *  Drawn independently of note-heads (the terminal recall cue survives their fade). */
  chordSymbols: { x: number; y: number; text: string; alpha: number }[];
}

export interface StaffFrameInput {
  song: Song;
  /** Hand-filtered, transpose-applied notes (engine.notes). */
  notes: readonly NoteEvent[];
  playhead: number;
  judgement: (n: NoteEvent) => Judgement | null;
  recentWrong: readonly { pitch: number; time: number }[];
  rect: StageRects;
  width: number;
  /** Page-mode systems to show; <=0 = fit-song. */
  rows: number;
  follow: "flow" | "page";
  /** Authoritative key (catalog tag); inferred from notes when undefined. */
  keyHint: string | undefined;
  /** Per-bar chord atom (null = no chord), aligned to bar index. Absent ⇒ no symbols. */
  chords?: readonly (string | null)[];
}

/** Future-note proximity alpha (faint far out, full-bright near the now-line). */
function futureAlpha(onset: number, playhead: number): number {
  const { proxNear, proxFar, faintAlpha } = CONFIG.staff;
  const d = Math.max(0, onset - playhead);
  if (d <= proxNear) return 1;
  if (d >= proxFar) return faintAlpha;
  return 1 - ((d - proxNear) / (proxFar - proxNear)) * (1 - faintAlpha);
}

/** Index of the beat-grid cell containing `t` (−1 if no grid) — the largest cell whose
 *  onset is ≤ t. Binary search (beats ascending): called per note + per bar every frame. */
function beatIndexOf(beats: readonly number[], t: number): number {
  if (beats.length < 2) return -1;
  let lo = 0;
  let hi = beats.length - 1;
  let ans = 0;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    if ((beats[m] ?? Number.POSITIVE_INFINITY) <= t) {
      ans = m;
      lo = m + 1;
    } else hi = m - 1;
  }
  return ans;
}

/** Local beat-grid spacing (seconds) bracketing `t`; 0 when unknown (<2 beats). */
function beatSpacing(beats: readonly number[], t: number): number {
  if (beats.length < 2) return 0;
  const c = beatIndexOf(beats, t);
  const a = beats[c];
  const b = beats[c + 1];
  if (a !== undefined && b !== undefined) return b - a;
  const last = beats[beats.length - 1];
  const prev = beats[beats.length - 2];
  return last !== undefined && prev !== undefined ? last - prev : 0;
}

/** Ledger-line Y positions for a note at `y` outside its staff (middleY ± 2 lines). */
function ledgerYs(y: number, middleY: number, lineGap: number): number[] {
  const k = (middleY - y) / lineGap; // line-units above the middle line (+ = up)
  const out: number[] = [];
  if (k > 2) for (let i = 3; i <= Math.floor(k + 0.01); i++) out.push(middleY - i * lineGap);
  else if (k < -2) for (let i = 3; i <= Math.floor(-k + 0.01); i++) out.push(middleY + i * lineGap);
  return out;
}

export function staffFrame(input: StaffFrameInput): StaffFrameModel {
  const { song, notes, playhead, rect, width, follow, rows } = input;
  const key = resolveKey(input.keyHint, notes);
  const layout = staffLayoutFor(song, CONFIG.staff.barsPerSystem, key);
  const noteheads: PlacedNote[] = [];
  const beamCands: {
    ni: number;
    x: number;
    y: number;
    middleY: number;
    sixteenth: boolean;
    key: string;
  }[] = [];

  const lineGap = CONFIG.staff.lineGapPx;
  const systemHeight = CONFIG.staff.staffGapPx + lineGap * 8;
  const currentSystem = layout.systemOf(Math.max(0, playhead));
  const visible =
    follow === "flow"
      ? { first: currentSystem, count: 1 }
      : pageVisible(currentSystem, rows, layout.systems.length);

  // Staff is centered horizontally and capped at maxWidthFraction of the canvas
  // (sheet-music-like, not stretched across a wide screen). The clef gutter sits
  // inside that block; `staffWidth` (not the screen width) drives the Flow camera so
  // the now-line + scroll speed are relative to the block.
  const staffWidth = Math.round(width * CONFIG.staff.maxWidthFraction);
  const originX = Math.round((width - staffWidth) / 2);
  const clefGutter = 40; // room for the vector clef glyphs left of the first note

  // Vertical: inset by padPx so the grand staff (and its ledger notes / now-line)
  // has breathing room above and below within the staff rect. The gap to the highway
  // / keyboard below is reserved by the layout (stage-layout belowStaffGapPx).
  const pad = CONFIG.staff.padPx;
  const usableTop = rect.staffTop + pad;
  const usableHeight = Math.max(systemHeight, rect.staffHeight - 2 * pad);
  const rowHeight = visible.count > 0 ? usableHeight / visible.count : usableHeight;
  const systems: StaffSystemBox[] = [];
  for (let r = 0; r < visible.count; r++) {
    const top = usableTop + r * rowHeight;
    const mid = top + rowHeight / 2;
    let trebleMiddleY = mid - systemHeight / 2 + lineGap * 2;
    let bassMiddleY = mid + systemHeight / 2 - lineGap * 2;
    if (follow === "flow") {
      // Place a step above middle C at the band center → melody (treble) is centered.
      const shift = mid - staffStepY("treble", CONFIG.staff.flowCenterStep, trebleMiddleY);
      trebleMiddleY += shift;
      bassMiddleY += shift;
    }
    systems.push({
      trebleMiddleY,
      bassMiddleY,
      x0: originX + clefGutter,
      x1: originX + staffWidth,
    });
  }

  const xInRow = (box: StaffSystemBox, t: number): number =>
    box.x0 + layout.xFrac(t) * (box.x1 - box.x0);
  const flowX = (t: number): number => originX + flowNoteX(t, playhead, staffWidth);

  const geom = (n: NoteEvent): { x: number; box: StaffSystemBox } | null => {
    if (follow === "flow") {
      const box = systems[0];
      return box ? { x: flowX(n.onset), box } : null;
    }
    const box = systems[layout.systemOf(n.onset) - visible.first];
    return box ? { x: xInRow(box, n.onset), box } : null;
  };

  // Per-bar metric info, so durations read in quarter-note units and beams group by
  // the felt beat. Compound meters (beats-per-bar a multiple of 3 and ≥6 → 6/8, 9/8,
  // 12/8 on an eighth grid) scale durations ×2 to quarters and group beams in 3s
  // (a beam per dotted-quarter). Heuristic — mis-handles rare 6/4 / 3/8 that need the
  // denominator; correct for x/4 and x/8 compound, the common repertoire.
  const barMetas: { start: number; startCell: number; groupSize: number; quarterMul: number }[] =
    [];
  for (let i = 0; i < song.bars.length - 1; i++) {
    const s = song.bars[i];
    const e = song.bars[i + 1];
    if (s === undefined || e === undefined) continue;
    const startCell = beatIndexOf(song.beats, s);
    const bpb = beatIndexOf(song.beats, e - 1e-6) - startCell + 1;
    const compound = bpb % 3 === 0 && bpb >= 6;
    barMetas.push({
      start: s,
      startCell,
      groupSize: compound ? 3 : 1,
      quarterMul: compound ? 2 : 1,
    });
  }
  const metaFor = (onset: number): (typeof barMetas)[number] | null => {
    let m: (typeof barMetas)[number] | null = barMetas[0] ?? null;
    for (const bm of barMetas) {
      if (bm.start <= onset) m = bm;
      else break;
    }
    return m;
  };

  // Beam candidates persist across a generous window beyond the visible edges, so a
  // group keeps all its members until fully off-screen (no pop as it scrolls past the
  // cull edge); noteheads still use the tight visible cull.
  const looseMargin = follow === "flow" ? staffWidth : 20;

  for (const n of notes) {
    const g = geom(n);
    if (!g) continue;
    const { x, box } = g;
    if (x < box.x0 - looseMargin || x > box.x1 + looseMargin) continue;
    const pos = staffPos(n, key, n.hand);
    const middleY = pos.clef === "treble" ? box.trebleMiddleY : box.bassMiddleY;
    const y = staffStepY(pos.clef, pos.step, middleY);
    const meta = metaFor(n.onset);
    const quarter = beatSpacing(song.beats, n.onset) * (meta?.quarterMul ?? 1);
    const glyph = quarter > 0 ? classifyNoteValue(n.duration / quarter) : null;

    let ni = -1;
    if (x >= box.x0 - 20 && x <= box.x1 + 20) {
      const j = input.judgement(n);
      let state: StaffNoteState;
      let alpha = 1;
      if (j === "perfect" || j === "good") state = "hit";
      else if (j === "miss") {
        state = "miss";
        const since = playhead - (n.onset + CONFIG.hitWindowMs / 1000);
        alpha = since < CONFIG.staff.missBlinkSeconds ? 1 : 0.5;
      } else if (Math.abs(n.onset - playhead) <= CONFIG.hitWindowMs / 1000) {
        state = "active";
      } else {
        state = "future";
        alpha = n.onset >= playhead ? futureAlpha(n.onset, playhead) : 0.4;
      }
      noteheads.push({
        x,
        y,
        state,
        alpha,
        accidental: pos.accidental,
        clef: pos.clef,
        ledgers: ledgerYs(y, middleY, lineGap),
        value: glyph?.value ?? null,
        dotted: glyph?.dotted ?? false,
        middleY,
        beamed: false,
        stemTipY: null,
      });
      ni = noteheads.length - 1;
    }
    if (glyph && (glyph.value === "eighth" || glyph.value === "sixteenth")) {
      const bi = beatIndexOf(song.beats, n.onset);
      if (bi >= 0) {
        const cell = meta
          ? meta.startCell + Math.floor((bi - meta.startCell) / meta.groupSize)
          : bi;
        beamCands.push({
          ni,
          x,
          y,
          middleY,
          sixteenth: glyph.value === "sixteenth",
          key: `${pos.clef}:${cell}`,
        });
      }
    }
  }

  // Wrong presses → transient red noteheads at the pressed pitch on the current row.
  const curBox = systems[follow === "flow" ? 0 : currentSystem - visible.first];
  for (const w of input.recentWrong) {
    const age = playhead - w.time;
    if (age < 0 || age > CONFIG.staff.wrongFadeSeconds || !curBox) continue;
    const wrongNote: NoteEvent = {
      midi: w.pitch,
      onset: w.time,
      duration: 0,
      group: 0,
      hand: "unknown",
      velocity: 0,
    };
    const pos = staffPos(wrongNote, key, "unknown");
    const middleY = pos.clef === "treble" ? curBox.trebleMiddleY : curBox.bassMiddleY;
    const x = follow === "flow" ? flowX(w.time) : xInRow(curBox, w.time);
    const wy = staffStepY(pos.clef, pos.step, middleY);
    noteheads.push({
      x,
      y: wy,
      state: "wrong",
      alpha: 1 - age / CONFIG.staff.wrongFadeSeconds,
      accidental: pos.accidental,
      clef: pos.clef,
      ledgers: ledgerYs(wy, middleY, lineGap),
      value: null,
      dotted: false,
      middleY,
      beamed: false,
      stemTipY: null,
    });
  }

  const markerBox = curBox ?? systems[0] ?? null;
  const nowMarker = markerBox
    ? {
        x:
          follow === "flow"
            ? originX + staffWidth * CONFIG.staff.nowLineFraction
            : xInRow(markerBox, playhead),
        y0: usableTop,
        y1: usableTop + rowHeight * (follow === "flow" ? 1 : visible.count),
      }
    : null;

  // Measure barlines: grand-staff-connecting verticals at each bar boundary, via
  // the same time→x maps as the noteheads. Page: per visible system, interior +
  // closing boundaries (skip the system start at x0). Flow: bars in the block.
  const barTop = 2 * lineGap;
  const barlines: { x: number; y0: number; y1: number }[] = [];
  const pushBar = (box: StaffSystemBox, x: number): void => {
    if (x < box.x0 || x > box.x1 + 0.5) return;
    barlines.push({ x, y0: box.trebleMiddleY - barTop, y1: box.bassMiddleY + barTop });
  };
  if (follow === "flow") {
    const box = systems[0];
    if (box) {
      for (let b = 1; b < song.bars.length; b++) {
        const t = song.bars[b];
        if (t !== undefined) pushBar(box, flowX(t));
      }
    }
  } else {
    for (let i = 0; i < visible.count; i++) {
      const box = systems[i];
      const sys = layout.systems[visible.first + i];
      if (!box || !sys) continue;
      // Interior boundaries map cleanly via xInRow…
      for (let b = sys.barStart + 1; b < sys.barEnd; b++) {
        const t = song.bars[b];
        if (t !== undefined) pushBar(box, xInRow(box, t));
      }
      // …but the closing time (tEnd) belongs to the next system, so its barline
      // sits at this system's right edge.
      if (sys.barEnd > sys.barStart) pushBar(box, box.x1);
    }
  }

  // Beam groups: per "<clef>:<beat>" run of ≥2 eighth/sixteenth onsets. Engraving
  // rules applied: stem direction from the note furthest off the middle line; a
  // gentle, capped slant taken from the OUTER notes (so a V/inverted-V beams flat);
  // the shortest stem ≈ default so the beam clears every head; a partial secondary
  // beam over the sixteenth-note sub-span. Approximate (per-clef, not voice-separated).
  const beams: StaffFrameModel["beams"] = [];
  const r2 = Math.max(2.5, lineGap / 2 - 0.5); // match drawStaff notehead radius
  const stem = lineGap * CONFIG.staff.stemLenGaps;
  const maxSlant = lineGap * CONFIG.staff.beamMaxSlantGaps;
  const groups = new Map<string, typeof beamCands>();
  for (const c of beamCands) {
    const g = groups.get(c.key);
    if (g) g.push(c);
    else groups.set(c.key, [c]);
  }
  for (const grp of groups.values()) {
    if (new Set(grp.map((c) => c.x)).size < 2) continue;
    const g = [...grp].sort((a, b) => a.x - b.x);
    // Direction: the note furthest from its staff middle line wins.
    let dev = 0;
    for (const c of g) if (Math.abs(c.y - c.middleY) > Math.abs(dev)) dev = c.y - c.middleY;
    const up = dev >= 0;
    const off = r2 * 0.95;
    const sx = (c: (typeof g)[number]): number => c.x + (up ? off : -off);
    const first = g[0];
    const last = g[g.length - 1];
    if (!first || !last) continue;
    const x0 = sx(first);
    const x1 = sx(last);
    // Slant from the outer notes, dampened + capped (≈flat when first≈last).
    let dy = (last.y - first.y) * CONFIG.staff.beamSlantDampen;
    dy = Math.max(-maxSlant, Math.min(maxSlant, dy));
    const slope = x1 !== x0 ? dy / (x1 - x0) : 0;
    // Intercept so the shortest stem ≈ `stem` and the beam clears every head.
    const tipAt = (c: (typeof g)[number]): number =>
      c.y + (up ? -stem : stem) - slope * (sx(c) - x0);
    const a = up ? Math.min(...g.map(tipAt)) : Math.max(...g.map(tipAt));
    const beamY = (x: number): number => a + slope * (x - x0);
    const sixes = g.filter((c) => c.sixteenth).map(sx);
    const visNh = g.map((c) => noteheads[c.ni]).find(Boolean); // first on-screen member
    beams.push({
      x0,
      y0: beamY(x0),
      x1,
      y1: beamY(x1),
      up,
      secondary: sixes.length > 0 ? { x0: Math.min(...sixes), x1: Math.max(...sixes) } : null,
      state: visNh?.state ?? "future",
      alpha: visNh?.alpha ?? 1,
    });
    for (const c of g) {
      const nh = noteheads[c.ni];
      if (nh) {
        nh.beamed = true;
        nh.stemTipY = beamY(sx(c));
      }
    }
  }

  // Lead-sheet chord symbols: place each bar's atom at the bar's start time, above
  // the treble staff, via the same time→x maps + visible-system cull as the barlines.
  const chordSymbols: StaffFrameModel["chordSymbols"] = [];
  const chords = input.chords;
  if (chords && chords.length > 0) {
    const symY = (box: StaffSystemBox): number => box.trebleMiddleY - lineGap * 3.5;
    const pushSym = (box: StaffSystemBox, x: number, atom: string, t: number): void => {
      if (x < box.x0 - 4 || x > box.x1 + 0.5) return;
      const alpha = t >= playhead ? futureAlpha(t, playhead) : 1;
      chordSymbols.push({ x, y: symY(box), text: chordLabel(atom), alpha });
    };
    if (follow === "flow") {
      const box = systems[0];
      if (box) {
        for (let i = 0; i < chords.length; i++) {
          const atom = chords[i];
          const t = song.bars[i];
          if (atom && t !== undefined) pushSym(box, flowX(t), atom, t);
        }
      }
    } else {
      for (let s = 0; s < visible.count; s++) {
        const box = systems[s];
        const sys = layout.systems[visible.first + s];
        if (!box || !sys) continue;
        for (let i = sys.barStart; i < sys.barEnd; i++) {
          const atom = chords[i];
          const t = song.bars[i];
          if (atom && t !== undefined) pushSym(box, xInRow(box, t), atom, t);
        }
      }
    }
  }

  return { systems, noteheads, nowMarker, barlines, beams, chordSymbols };
}
